import { defineComponent as _defineComponent } from 'vue'
import {isAuthorized, t} from "@/mixin/mixins";
  import InvCreateDialogFormCard from "@/views/Orders/Calendar/InvCreateDialogFormCard.vue";
  import {computed, reactive, ref, toRefs, watch} from "vue";
  import Order, {OrderSendMail} from "@/model/Order";
  import moment from "moment";
  import CustomerLookup from "@/components/CustomerLookup.vue";
  import ConfirmDialog from "@/components/ConfirmDialog.vue";
  import useVuelidate from "@vuelidate/core";
  import {integer, minValue, required} from "@vuelidate/validators";
  import FormListEntry from "@/components/FormListEntry.vue";
  import {
    createDefaultInventoryOrder,
    updateInventoryOrderCustomer,
    updatePositions
  } from "@/views/Orders/Calendar/OrderCalendarService";
  import OrderService from "@/service/OrderService";
  import OrderServiceV2, {toCreateOrderRequestV3} from "@/service/OrderServiceV2";
  import {success} from "@/MessageUtils";
  import Customer from "@/model/Customer";
  import {AxiosError} from "axios";
  import eventBus from "@/eventbus";
  import {MessageTypes} from "@/components/message-types";

  
export default /*#__PURE__*/_defineComponent({
  __name: 'InvCreateDialogInv',
  props: {
    isVisible: {
      type: Boolean,
      default: () => false
    },
    clickedDay: {
      type: String,
      default: () => ""
    },
    clickedOrder: {
      type: Order,
      default: () => undefined
    },
    customer: {
      type: Customer,
      default: () => undefined
    },
    positions: {
      type: Number,
      default: () => 0
    }
  },
  emits: ["saveInv"],
  setup(__props, { expose, emit }) {

const props = __props

  const isEditable = computed(() => {
    return isAuthorized("Order_Overview_EditOrders") || isAuthorized("Superadmin");
  });
  const menuDateValidFrom = ref<string | null>("");
  const menuDateValidTo = ref<string | null>("");
  const menuStartTime = ref<boolean>();
  const confirmDialog = ref<InstanceType<typeof ConfirmDialog>>();
  const customerLookup = ref<InstanceType<typeof CustomerLookup>>();

  const errors = computed(() => {
    const map = new Map<string, string>();
    // @ts-ignore
    v$.value.$errors.forEach(error => map.set(error.$property, error.$message as string));
    return map;
  });

  

  const isVisible = computed({
    get: () => props.isVisible,
    set: (value) => {
    }
  });

  const clickedOrder = computed({
    get: () => props.clickedOrder,
    set: (value) => {
    }
  });

  const minDate = computed({
    get: () => moment().startOf("month").format("YYYY-MM-DD"),
    set: (value) => {
    }
  });
  const addCustomer = (): void => {
    customerLookup.value?.open()
      .then((result: any) => {
        if (result) {
          if (state.currentInventory) {
            if ((state.currentInventory?.customer == null || state.currentInventory?.customer?.id != result.id)) {
              updateInventoryOrderCustomer(state.currentInventory, result.uuid);
              updatePositions(state.currentInventory, result.uuid);
            }
          }
        }
      });
  };

  

  const removeCustomer = () => {
    const title = t("order.orderInfo.customer.removeCustomerConfirmationDialog.title");
    const text = t("order.orderInfo.customer.removeCustomerConfirmationDialog.text");
    confirmDialog.value?.open(title, text)
      .then((confirmResult) => {
        if (confirmResult) {
          if (state.currentInventory) {
            state.currentInventory.customer = null;
          }
          save();
        }
      });
  };

  const initialState = {
    originalInventory: null as Order | null,
    currentInventory: null as Order | null,
    latestInventory: null as Order | null,
    sendMail: true,
    serverErrors: [],
  };

  const state = reactive({...initialState});

  toRefs(state);

  const rules = {
    currentInventory: {
      name: {
        required
      },
      address: {
        required
      },
      zip: {
        required
      },
      city: {
        required
      },
      positions: {
        integer,
        minValue: minValue(1)
      },
      validFrom: {
        required
      },
      validTo: {
        required
      }
    }
  };

  const v$ = useVuelidate(rules, state);

  const save = () => {
    v$.value.$reset();
    v$.value.$touch();

    if (!v$.value.$invalid && state.currentInventory) {
      const promise = (clickedOrder.value) ? updateInventory(clickedOrder.value.id, state.currentInventory)
        : createInventory(state.currentInventory);
      promise
        .then(order => {
          success(t("saveSuccessful"));
          emit("saveInv", order, state.originalInventory);
        })
        .catch((error: AxiosError<never[]>) => {
          if (error.response?.status === 422) {
            state.serverErrors = error.response.data;
            eventBus.$emit(MessageTypes.WARNING, t("checkInput"));
          }
        });
    }
  };

  const updateInventory = (orderId: number, inventory: Order): Promise<Order> => {
    inventory.type = "Project";
    const request: OrderSendMail = {
      ...inventory,
      sendMail: state.sendMail
    };
    return OrderService.updateOrder(orderId, request)
      .then(response => response.data);
  };

  // POST /v3/orders answers with the order's id, name, type and validity only, the calendar shows the whole order
  const createInventory = (inventory: Order): Promise<Order> => {
    return OrderServiceV2.createOrder(toCreateOrderRequestV3(inventory, state.sendMail))
      .then(response => OrderServiceV2.getOrder(response.data.legacyId as number))
      .then(response => response.data);
  };

  watch(() => state.originalInventory, (newValue, oldValue) => {
    if (newValue) {
      state.currentInventory = Object.assign(new Order(), JSON.parse(JSON.stringify(newValue)));
      if (state.currentInventory && props.customer) {
        if (state.currentInventory.customer == null) {
          state.currentInventory.customer = Object.assign(new Customer(), JSON.parse(JSON.stringify(props.customer)));
          state.currentInventory.name = props.customer.name;
          state.currentInventory.address = props.customer.address.street;
          state.currentInventory.zip = props.customer.address.zip as unknown as string | null;
          state.currentInventory.city = props.customer.address.city as unknown as string | null;
          state.currentInventory.positions = props.positions ? props.positions : 0;

          const customerCategory = {
            fruitAndVegetables: props.customer.og ?? false,
            flowers: props.customer.flowers ?? false,
            storage: props.customer.storage ?? false,
            targetActualComparison: props.customer.targetActualComparison ?? false,
            labels: props.customer.labels ?? false,
            drinks: props.customer.drinks ?? false,
          };
          state.currentInventory.customerCategory = customerCategory;
        }
      }
    } else {
      state.currentInventory = null;
    }
  }, {immediate: true, deep: true});

  watch(() => isVisible.value, (newValue, oldValue) => {
    if (isVisible.value) {
      if (clickedOrder.value) {
        state.originalInventory = Object.assign(new Order(), JSON.parse(JSON.stringify(clickedOrder.value)));
      } else {
        state.originalInventory = createDefaultInventoryOrder(null, props.clickedDay, props.clickedDay);
      }
    } else {
      state.originalInventory = null;
    }
  }, {immediate: true});

  const currentInventoryName = computed({
    get: () => {
      return state.currentInventory ? state.currentInventory.name : "";
    },
    set: (newValue) => {
      if (state.currentInventory) {
        state.currentInventory.name = newValue;
      }
    }
  });

  const currentInventoryAddress = computed({
    get: () => {
      return state.currentInventory ? state.currentInventory.address : "";
    },
    set: (newValue) => {
      if (state.currentInventory) {
        state.currentInventory.address = newValue;
      }
    }
  });

  const currentInventoryZip = computed({
    get: () => {
      return state.currentInventory ? state.currentInventory.zip : "";
    },
    set: (newValue) => {
      if (state.currentInventory) {
        state.currentInventory.zip = newValue;
      }
    }
  });

  const currentInventoryCity = computed({
    get: () => {
      return state.currentInventory ? state.currentInventory.city : "";
    },
    set: (newValue) => {
      if (state.currentInventory) {
        state.currentInventory.city = newValue;
      }
    }
  });

  const currentInventoryPositions = computed({
    get: () => {
      return state.currentInventory ? state.currentInventory.positions : 0;
    },
    set: (newValue) => {
      if (state.currentInventory) {
        state.currentInventory.positions = newValue;
      }
    }
  });

  const currentInventoryValidFromTextfield = computed(() => {
    return state.currentInventory ? moment(state.currentInventory.validFrom).format("DD.MM.YYYY") : "";
  });

  const currentInventoryValidFromDatePicker = computed({
    get: () => {
      return state.currentInventory ? moment(state.currentInventory.validFrom).format("YYYY-MM-DD") : "";
    },
    set: (newValue) => {
      if (state.currentInventory) {
        state.currentInventory.validFrom = newValue;
      }
    }
  });

  const currentInventoryValidToTextfield = computed(() => {
    return state.currentInventory ? moment(state.currentInventory.validTo).format("DD.MM.YYYY") : "";
  });

  const currentInventoryValidToDatePicker = computed({
    get: () => {
      return state.currentInventory ? moment(state.currentInventory.validTo).format("YYYY-MM-DD") : "";
    },
    set: (newValue) => {
      if (state.currentInventory) {
        state.currentInventory.validTo = newValue;
      }
    }
  });

  const currentInventoryStartTimePicker = computed({
    get: () => {
      return state.currentInventory ? state.currentInventory.startTime : "";
    },
    set: (newValue) => {
      if (state.currentInventory) {
        state.currentInventory.startTime = newValue;
      }
    }
  });

  expose({
    save
  });

return { __sfc: true,isEditable, menuDateValidFrom, menuDateValidTo, menuStartTime, confirmDialog, customerLookup, errors, props, isVisible, clickedOrder, minDate, addCustomer, emit, removeCustomer, initialState, state, rules, v$, save, updateInventory, createInventory, currentInventoryName, currentInventoryAddress, currentInventoryZip, currentInventoryCity, currentInventoryPositions, currentInventoryValidFromTextfield, currentInventoryValidFromDatePicker, currentInventoryValidToTextfield, currentInventoryValidToDatePicker, currentInventoryStartTimePicker, t, InvCreateDialogFormCard, CustomerLookup, ConfirmDialog, FormListEntry }
}

})